import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ChallengesPopupGate } from '@/components/fantasy/ChallengesPopupGate';
import { PitchView } from '@/components/fantasy/PitchView';
import { useWcEliminatedTeams } from '@/features/wc/eliminations';
import { useCompetitions } from '@/features/leagues/hooks';
import { useThemePalette } from '@/config/theme';
import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Segmented } from '@/components/ui/Segmented';
import { Skeleton } from '@/components/ui/Skeleton';
import { isSupabaseConfigured } from '@/config';
import {
  pickCurrentGameweek,
  useFantasyScoresRealtime,
  useGameweeks,
  useMyFantasyTeams,
  useMyRank,
  usePlayerGwScores,
  useSpinState,
  useSquadPicks,
  useTeamGwScore,
} from '@/features/fantasy/hooks';
import { useProvisionalGwPoints } from '@/features/fantasy/provisional';
import { parseFormationRules } from '@/features/fantasy/rules';
import { draftFromPicks } from '@/features/fantasy/squadModel';
import { SLOT_LABELS_442 } from '@/features/fantasy/worldCup';
import { useAuth } from '@/lib/auth/AuthProvider';
import { breakdownSchema, parseOrNull } from '@/lib/data/schemas';
import { formatNumber, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { Position } from '@scoring/rules.ts';

export default function FantasyScreen() {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { userId } = useAuth();
  const { numeralOptions, arabicNumerals } = useLocale();
  const insets = useSafeAreaInsets();

  const teamsQuery = useMyFantasyTeams();
  const teams = teamsQuery.data ?? [];
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const team = teams.find((entry) => entry.id === selectedTeamId) ?? teams[0];
  const season = team?.fantasy_season ?? null;

  const gameweeksQuery = useGameweeks(season?.id);
  const currentGw = pickCurrentGameweek(gameweeksQuery.data);
  const picksQuery = useSquadPicks(team?.id);
  const gwScores = usePlayerGwScores(currentGw?.id);
  const teamGwScoreQuery = useTeamGwScore(team?.id, currentGw?.id);
  const rankQuery = useMyRank(season?.id);
  const spinState = useSpinState(season?.id);
  useFantasyScoresRealtime(currentGw?.id);

  const draft = useMemo(() => draftFromPicks(picksQuery.data ?? []), [picksQuery.data]);
  const rules = useMemo(() => parseFormationRules(season?.formation_rules), [season]);

  // Soft-warn when squad players are from knocked-out WC nations. isEliminated
  // only ever returns true for WC teams, so this is implicitly WC-only.
  const { isEliminated } = useWcEliminatedTeams();
  const eliminatedCount = useMemo(
    () => draft.picks.filter((pick) => isEliminated(pick.player.team?.id)).length,
    [draft, isEliminated],
  );

  const playerPositions = useMemo(() => {
    const map = new Map<string, Position>();
    for (const pick of draft.picks) {
      map.set(pick.player.id, pick.player.position);
    }
    return map;
  }, [draft]);

  const provisional = useProvisionalGwPoints({
    competitionId: season?.competition_id,
    season: season?.season,
    roundLabel: currentGw?.round_label,
    playerPositions,
    rules: rules.scoring,
  });

  const [breakdownPlayerId, setBreakdownPlayerId] = useState<string | null>(null);
  const [showPhotos, setShowPhotos] = useState(true);
  // WC squads toggle to nation FLAGS; club leagues toggle to club CRESTS.
  const isWorldCup = (season?.competition?.name ?? '').toLowerCase().includes('world cup');

  // The engine's number (scored from the LOCKED lineup) always wins; the
  // client-side sum over current picks is only a stand-in until it lands.
  const officialTotal = useMemo(() => {
    if (teamGwScoreQuery.data) {
      return teamGwScoreQuery.data.points;
    }
    let total = 0;
    let any = false;
    for (const pick of draft.picks) {
      if (!pick.isStarting) continue;
      const score = gwScores.byPlayer.get(pick.player.id);
      if (score) {
        any = true;
        total += score.points * (pick.isCaptain ? rules.scoring.captainMultiplier : 1);
      }
    }
    return any ? total : null;
  }, [teamGwScoreQuery.data, draft, gwScores.byPlayer, rules]);

  const provisionalTotal = useMemo(() => {
    let total = 0;
    for (const pick of draft.picks) {
      if (!pick.isStarting) continue;
      const value = provisional.points.get(pick.player.id) ?? 0;
      total += value * (pick.isCaptain ? rules.scoring.captainMultiplier : 1);
    }
    return total;
  }, [draft, provisional.points, rules]);

  const pointsFor = (playerId: string): string => {
    const official = gwScores.byPlayer.get(playerId);
    if (official) {
      return localizeDigits(String(official.points), arabicNumerals);
    }
    const live = provisional.points.get(playerId);
    if (live !== undefined && live !== 0) {
      return `~${localizeDigits(String(live), arabicNumerals)}`;
    }
    return '-';
  };

  const breakdownScore = breakdownPlayerId ? gwScores.byPlayer.get(breakdownPlayerId) : undefined;
  const breakdownEntries = breakdownScore
    ? (parseOrNull(breakdownSchema, breakdownScore.breakdown) ?? [])
    : [];

  if (!isSupabaseConfigured) {
    return (
      <Screen>
        <View className="px-4 pb-3 pt-2">
          <AppText variant="title">{t('fantasy.title')}</AppText>
        </View>
        <EmptyState
          icon="settings-outline"
          title={t('common.notConfiguredTitle')}
          body={t('common.notConfiguredBody')}
        />
      </Screen>
    );
  }

  if (!userId) {
    return (
      <Screen>
        <View className="px-4 pb-3 pt-2">
          <AppText variant="title">{t('fantasy.title')}</AppText>
        </View>
        <EmptyState
          icon="lock-closed-outline"
          title={t('common.signInRequiredTitle')}
          body={t('common.signInRequiredBody')}
          actionLabel={t('auth.signIn')}
          onAction={() => router.push('/auth/sign-in')}
        />
      </Screen>
    );
  }

  if (teamsQuery.isPending) {
    return (
      <Screen>
        <View className="px-4 pb-3 pt-2">
          <AppText variant="title">{t('fantasy.title')}</AppText>
        </View>
        <View className="gap-4 px-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-72 w-full" />
        </View>
      </Screen>
    );
  }

  if (teamsQuery.isError) {
    return (
      <Screen>
        <View className="px-4 pb-3 pt-2">
          <AppText variant="title">{t('fantasy.title')}</AppText>
        </View>
        <ErrorState error={teamsQuery.error} onRetry={() => teamsQuery.refetch()} />
      </Screen>
    );
  }

  if (!team || !season) {
    return (
      <Screen>
        <View className="px-4 pb-3 pt-2">
          <AppText variant="title">{t('fantasy.title')}</AppText>
        </View>
        <CompetitionChooser />
        {/* Spin Boost teaser: surface the daily mini-game even BEFORE a team
            exists, so it is discoverable. Tapping funnels to team creation. */}
        <Pressable
          accessibilityRole="button"
          onPress={() => router.push('/fantasy/onboarding')}
          className="mx-4 mb-1 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3.5 active:opacity-80"
        >
          <Ionicons name="sync-circle" size={22} color={palette.voltBright} />
          <View className="flex-1">
            <AppText variant="caption" weight="bold" className="text-ink">
              {t('spin.title')}
            </AppText>
            <AppText variant="micro" className="text-soft" numberOfLines={2}>
              {t('spin.cardBody')}
            </AppText>
          </View>
          <Ionicons name="chevron-forward" size={16} color={palette.muted} />
        </Pressable>
        <EmptyState
          icon="trophy-outline"
          title={t('fantasy.myTeam.noTeamTitle')}
          body={t('fantasy.myTeam.noTeamBody')}
          actionLabel={t('fantasy.myTeam.noTeamCta')}
          onAction={() => router.push('/fantasy/onboarding')}
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <ChallengesPopupGate />
      <View className="flex-row items-center justify-between px-4 pb-3 pt-2">
        <AppText variant="title">{t('fantasy.myTeam.title')}</AppText>
        <Button
          label={t('fantasy.myTeam.editSquad')}
          variant="secondary"
          compact
          onPress={() => router.push({ pathname: '/fantasy/builder', params: { seasonId: season.id } })}
        />
      </View>

      {currentGw?.status === 'live' ? (
        <View className="mx-4 mb-3 flex-row items-center gap-2 rounded-card border border-line bg-surface px-3 py-2.5">
          <Ionicons name="lock-closed" size={15} color={palette.captain} />
          <AppText variant="micro" weight="medium" className="flex-1 text-soft">
            {t('fantasy.myTeam.roundLocked')}
          </AppText>
        </View>
      ) : null}

      <CompetitionChooser />

      {teams.length > 1 ? (
        <View className="pb-3">
          <Segmented
            options={teams.map((entry) => ({
              value: entry.id,
              label: entry.fantasy_season?.competition?.name ?? entry.name,
            }))}
            value={team.id}
            onChange={setSelectedTeamId}
          />
        </View>
      ) : null}

      <ScrollView contentContainerStyle={{ paddingBottom: insets.bottom + 32 }}>
        <Card className="mx-4 mb-4 px-4 py-4">
          <AppText variant="heading" className="mb-3">
            {team.name}
          </AppText>
          <View className="flex-row gap-3">
            <View className="flex-1 items-center rounded-2xl bg-surface-2 px-2 py-3">
              <AppText variant="title" weight="bold" tabular className="text-live">
                {officialTotal !== null
                  ? formatNumber(officialTotal, numeralOptions)
                  : provisional.anyLive
                    ? `~${formatNumber(provisionalTotal, numeralOptions)}`
                    : '-'}
              </AppText>
              <AppText variant="micro" className="text-center">
                {t('fantasy.myTeam.gwPoints')}
              </AppText>
            </View>
            <View className="flex-1 items-center rounded-2xl bg-surface-2 px-2 py-3">
              <AppText variant="title" weight="bold" tabular className="text-volt">
                {formatNumber(team.total_points, numeralOptions)}
              </AppText>
              <AppText variant="micro" className="text-center">
                {t('fantasy.myTeam.totalPoints')}
              </AppText>
            </View>
            <View className="flex-1 items-center rounded-2xl bg-surface-2 px-2 py-3">
              <AppText variant="title" weight="bold" tabular className="text-ink">
                {rankQuery.data
                  ? formatNumber(rankQuery.data.rank, numeralOptions)
                  : '-'}
              </AppText>
              <AppText variant="micro" className="text-center">
                {t('fantasy.myTeam.overallRank')}
              </AppText>
            </View>
          </View>
          {provisional.anyLive && officialTotal === null ? (
            <AppText variant="micro" className="mt-3 text-live">
              {t('fantasy.myTeam.provisional')}
            </AppText>
          ) : null}
        </Card>

        {/* captain hint banner (Volt design) */}
        {(() => {
          const captain = draft.picks.find((pick) => pick.isCaptain);
          if (!captain) return null;
          return (
            <View className="mx-4 mb-3 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt/10 px-3.5 py-2.5">
              <AppText variant="micro" weight="bold" className="text-volt-bright">
                C
              </AppText>
              <AppText variant="micro" className="flex-1 text-soft" numberOfLines={1}>
                {t('fantasy.myTeam.captainHint', {
                  multiplier: localizeDigits(
                    String(rules.scoring.captainMultiplier),
                    arabicNumerals,
                  ),
                })}
              </AppText>
              <AppText variant="micro" weight="bold" className="text-volt-bright" numberOfLines={1}>
                {captain.player.known_as ?? captain.player.name}
              </AppText>
            </View>
          );
        })()}

        {/* Knocked-out-nation warning: soft-marks (never blocks) squad players
            whose WC nation is eliminated. WC-only by construction (isEliminated
            only matches WC teams). */}
        {eliminatedCount > 0 ? (
          <View className="mx-4 mb-3 flex-row items-center gap-3 rounded-card border border-loss/40 bg-loss/10 px-3.5 py-2.5">
            <Ionicons name="alert-circle" size={16} color={palette.loss} />
            <AppText variant="micro" weight="medium" className="flex-1 text-soft" numberOfLines={2}>
              {t('wc.fantasyEliminatedWarning', { count: eliminatedCount })}
            </AppText>
          </View>
        ) : null}

        {/* Spin Boost: the 24h bonus mini-game (WC only) - kept prominent so it
            is easy to find right under the team. */}
        {isWorldCup ? (
          <Pressable
            accessibilityRole="button"
            onPress={() =>
              router.push({ pathname: '/fantasy/spin', params: { seasonId: season.id } })
            }
            className="mx-4 mb-3 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3.5 active:opacity-80"
          >
            <Ionicons name="sync-circle" size={22} color={palette.voltBright} />
            <View className="flex-1">
              <AppText variant="caption" weight="bold" className="text-ink">
                {t('spin.title')}
              </AppText>
              <AppText variant="micro" className="text-soft">
                {t('spin.cardBody')}
              </AppText>
            </View>
            {spinState.data?.can_spin ? (
              <View className="rounded-full bg-volt px-2 py-0.5">
                <AppText variant="micro" weight="bold" className="text-on-volt">
                  {t('spin.ready')}
                </AppText>
              </View>
            ) : (
              <Ionicons name="chevron-forward" size={16} color={palette.muted} />
            )}
          </Pressable>
        ) : null}

        {picksQuery.isPending ? (
          <View className="px-4">
            <Skeleton className="h-72 w-full" />
          </View>
        ) : picksQuery.isError ? (
          <ErrorState error={picksQuery.error} onRetry={() => picksQuery.refetch()} />
        ) : draft.picks.length === 0 ? (
          <EmptyState
            icon="trophy-outline"
            title={t('fantasy.myTeam.noTeamTitle')}
            body={t('fantasy.myTeam.noTeamBody')}
            actionLabel={t('fantasy.myTeam.noTeamCta')}
            onAction={() =>
              router.push({ pathname: '/fantasy/builder', params: { seasonId: season.id } })
            }
          />
        ) : (
          <>
            <View className="mx-4 mb-2 flex-row items-center justify-end">
              <View className="flex-row rounded-full border border-line bg-inset p-0.5">
                {([true, false] as const).map((on) => {
                  const active = on === showPhotos;
                  return (
                    <Pressable
                      key={String(on)}
                      accessibilityRole="button"
                      accessibilityState={{ selected: active }}
                      accessibilityLabel={t(
                        on
                          ? 'fantasy.myTeam.viewPhotos'
                          : isWorldCup
                            ? 'fantasy.myTeam.viewFlags'
                            : 'fantasy.myTeam.viewClubs',
                      )}
                      onPress={() => setShowPhotos(on)}
                      className={`h-7 w-9 items-center justify-center rounded-full ${active ? 'bg-volt' : ''}`}
                    >
                      <Ionicons
                        name={on ? 'person' : isWorldCup ? 'flag' : 'shield'}
                        size={14}
                        color={active ? palette.onVolt : palette.muted}
                      />
                    </Pressable>
                  );
                })}
              </View>
            </View>
            <PitchView
              draft={draft}
              rules={rules}
              mode="points"
              slotLabels={isWorldCup ? SLOT_LABELS_442 : undefined}
              pointsFor={pointsFor}
              showPhotos={showPhotos}
              onChipPress={setBreakdownPlayerId}
            />
          </>
        )}

        <View className="mx-4 mt-4 flex-row gap-3">
          <Button
            label={
              currentGw
                ? t('fantasy.gameweek.title', {
                    number: localizeDigits(String(currentGw.number), arabicNumerals),
                  })
                : t('fantasy.gameweek.noGameweekTitle')
            }
            variant="secondary"
            compact
            className="flex-1"
            disabled={!currentGw}
            onPress={() =>
              router.push({ pathname: '/fantasy/gameweek', params: { seasonId: season.id } })
            }
          />
          <Button
            label={t('fantasy.leaderboards.title')}
            variant="secondary"
            compact
            className="flex-1"
            onPress={() =>
              router.push({ pathname: '/fantasy/leaderboards', params: { seasonId: season.id } })
            }
          />
        </View>

        <Pressable
          accessibilityRole="button"
          onPress={() => router.push('/fantasy/challenges' as never)}
          className="mx-4 mt-3 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3.5 active:opacity-80"
        >
          <Ionicons name="flash" size={20} color={palette.voltBright} />
          <View className="flex-1">
            <AppText variant="caption" weight="bold" className="text-ink">
              {t('challenges.title')}
            </AppText>
            <AppText variant="micro" className="text-soft">
              {t('challenges.howItWorks')}
            </AppText>
          </View>
          <Ionicons name="chevron-forward" size={16} color={palette.muted} />
        </Pressable>

      </ScrollView>

      {/* Points breakdown sheet */}
      <Modal
        visible={breakdownPlayerId !== null}
        animationType="fade"
        transparent
        onRequestClose={() => setBreakdownPlayerId(null)}
      >
        <Pressable
          className="flex-1 justify-end bg-black/60"
          onPress={() => setBreakdownPlayerId(null)}
        >
          <Pressable
            onPress={(event) => event.stopPropagation()}
            className="rounded-t-3xl border border-line bg-surface-2 px-4 pt-5"
            style={{ paddingBottom: insets.bottom + 20 }}
          >
            <AppText variant="heading" className="mb-3">
              {t('fantasy.myTeam.pointsBreakdown')}
            </AppText>
            {breakdownEntries.length === 0 ? (
              <AppText variant="caption" className="pb-2">
                {t('fantasy.myTeam.noPointsYet')}
              </AppText>
            ) : (
              breakdownEntries.map((entry, index) => (
                <View
                  key={index}
                  className="flex-row items-center justify-between border-b border-line py-2.5"
                >
                  <AppText variant="caption" className="text-ink">
                    {t(`fantasy.myTeam.breakdown.${entry.reason}`, {
                      defaultValue: entry.reason,
                    })}
                    {entry.reason === 'minutes' || entry.value > 1
                      ? `  (${localizeDigits(String(entry.value), arabicNumerals)})`
                      : ''}
                  </AppText>
                  <AppText
                    variant="caption"
                    weight="bold"
                    tabular
                    className={entry.points >= 0 ? 'text-win' : 'text-loss'}
                  >
                    {entry.points > 0 ? '+' : ''}
                    {localizeDigits(String(entry.points), arabicNumerals)}
                  </AppText>
                </View>
              ))
            )}
            <Button
              label={t('common.close')}
              variant="secondary"
              compact
              className="mt-4"
              onPress={() => setBreakdownPlayerId(null)}
            />
          </Pressable>
        </Pressable>
      </Modal>
    </Screen>
  );
}

/**
 * Competition chooser strip: the World Cup is live; the league game is built but
 * locked for launch (the WC is the headline event). Shown as two pills so the
 * structure reads clearly even while EPL is "coming soon".
 */
const EPL_LOGO = 'https://media.api-sports.io/football/leagues/39.png';
const WC_PROVIDER_ID = 9900001;

function CompetitionChooser() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const competitions = useCompetitions();
  const wcLogo =
    (competitions.data ?? []).find((c) => c.provider_id === WC_PROVIDER_ID)?.logo_url ?? null;
  return (
    <View className="mb-3 flex-row gap-2.5 px-4">
      {/* World Cup: the active game */}
      <View className="flex-1 overflow-hidden rounded-2xl border border-volt-line">
        <LinearGradient
          colors={['rgba(34,211,255,0.24)', 'rgba(138,43,226,0.14)', 'rgba(20,20,42,0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, paddingVertical: 11 }}
        >
          {wcLogo ? (
            <Image source={{ uri: wcLogo }} style={{ width: 26, height: 26 }} contentFit="contain" cachePolicy="memory-disk" />
          ) : (
            <Ionicons name="earth" size={18} color={palette.voltBright} />
          )}
          <AppText variant="caption" weight="bold" className="flex-1 text-volt-bright" numberOfLines={1}>
            {t('fantasy.competitions.worldCup')}
          </AppText>
          <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: palette.volt }} />
        </LinearGradient>
      </View>

      {/* Premier League: built but locked for launch */}
      <View className="flex-1 overflow-hidden rounded-2xl border border-line" style={{ opacity: 0.72 }}>
        <LinearGradient
          colors={['rgba(61,25,91,0.5)', 'rgba(214,0,255,0.08)', 'rgba(20,20,42,0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 11 }}
        >
          <Image source={{ uri: EPL_LOGO }} style={{ width: 24, height: 24 }} contentFit="contain" cachePolicy="memory-disk" />
          <AppText variant="caption" weight="bold" className="flex-1 text-soft" numberOfLines={1}>
            {t('fantasy.competitions.premierLeague')}
          </AppText>
          <View className="flex-row items-center gap-1 rounded-full bg-surface-2 px-1.5 py-0.5">
            <Ionicons name="lock-closed" size={9} color={palette.muted} />
            <AppText variant="micro" weight="bold" className="text-soft">
              {t('fantasy.competitions.soon')}
            </AppText>
          </View>
        </LinearGradient>
      </View>
    </View>
  );
}
